import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ContentBox from '../../components/ContentBox';
import {
    FrankoPopLayout, FpModal, FpButton, BoxArt, FigureFrame, FigureCard, RarityPill,
    StatTile, ProgressBar, EmptyState, TextInput, NumberInput,
} from '../../components/frankopop/frankopopShared';
import {
    useFrankopopData, fetchWallet, fetchMyBoxes, fetchMyPops, fetchMyStats, fetchSet,
    openBox, unpackagePop, quickSellManyPops, CURRENCY, FRAMP_UNIT, SCRAP_UNIT,
} from '../../data/frankopop';

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Boxes from the same set are stacked into one tile - opening it always opens the
// oldest box in that stack first (boxes already arrive sorted oldest-first).
function UnopenedBoxes({ boxes, onOpened }) {
    if (boxes.length === 0) return null;

    const groups = [];
    const bySetId = new Map();
    for (const box of boxes) {
        let group = bySetId.get(box.setId);
        if (!group) {
            group = { setId: box.setId, set: box.set, boxes: [] };
            bySetId.set(box.setId, group);
            groups.push(group);
        }
        group.boxes.push(box);
    }

    return (
        <ContentBox theme="default" tint="darkorange" header={`Unopened boxes (${boxes.length})`}>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {groups.map((group) => {
                    const oldest = group.boxes[0];
                    return (
                        <div key={group.setId} className="bg-white border border-gray-300 flex flex-col" style={{ boxShadow: '0 3px 0 0 rgba(0,0,0,0.15)' }}>
                            <div className="relative bg-gray-100 border-b border-gray-200 flex items-center justify-center p-3">
                                <BoxArt set={group.set} size={120} />
                                {group.boxes.length > 1 && (
                                    <span className="absolute top-2 right-2 font-['Jersey_10'] uppercase text-[11px] tracking-[1px] text-white px-2 py-0.5" style={{ backgroundColor: '#5A2929', boxShadow: '0 2px 0 0 rgba(0,0,0,0.25)' }}>
                                        {group.boxes.length}
                                    </span>
                                )}
                            </div>
                            <div className="p-3 flex flex-col items-center gap-1 flex-1 text-center">
                                <h3 className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] text-gray-900 leading-tight">{group.set?.name}</h3>
                                <p className="text-[11px] text-gray-400 font-['Jersey_10'] uppercase tracking-[1px]">
                                    {oldest.source === 'purchase' ? 'Bought' : oldest.source === 'admin_gift' ? `Gift from ${oldest.grantedBy}` : `Staff gift`}
                                </p>
                                <FpButton variant="primary" size="sm" className="w-full mt-auto" onClick={() => onOpened(oldest)}>Open box</FpButton>
                            </div>
                        </div>
                    );
                })}
            </div>
        </ContentBox>
    );
}

function QuickSellMenu({ count, onSellOne, onSellAllButOne, onSellQuantity }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const pick = (fn) => { setOpen(false); fn(); };

    return (
        <div className="relative" ref={ref}>
            <FpButton variant="ghost" size="sm" onClick={() => setOpen((o) => !o)}>Quick sell ▾</FpButton>
            {open && (
                <div className="absolute right-0 bottom-full mb-1 z-20 bg-white border border-gray-300 min-w-[180px]" style={{ boxShadow: '0 3px 0 0 rgba(0,0,0,0.25)' }}>
                    {count > 1 && (
                        <button type="button" onClick={() => pick(onSellAllButOne)} className="block w-full text-left px-3 py-2 text-xs font-['Jersey_10'] uppercase tracking-[1px] text-gray-700 hover:bg-gray-100">
                            Quick sell all but 1
                        </button>
                    )}
                    <button type="button" onClick={() => pick(onSellOne)} className="block w-full text-left px-3 py-2 text-xs font-['Jersey_10'] uppercase tracking-[1px] text-gray-700 hover:bg-gray-100">
                        Quick sell 1
                    </button>
                    {count > 1 && (
                        <button type="button" onClick={() => pick(onSellQuantity)} className="block w-full text-left px-3 py-2 text-xs font-['Jersey_10'] uppercase tracking-[1px] text-gray-700 hover:bg-gray-100">
                            Quick sell quantity...
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function QuickSellQuantityModal({ max, onConfirm, onClose }) {
    const [value, setValue] = useState('1');
    const parsed = parseInt(value, 10);
    const valid = String(parsed) === value.trim() && Number.isInteger(parsed) && parsed > 0 && parsed <= max;

    return (
        <FpModal title="Quick sell quantity" onClose={onClose} maxWidth="max-w-xs">
            <p className="text-sm text-gray-600 mb-3">You own {max}. Enter how many to quick sell.</p>
            <NumberInput min={1} max={max} value={value} onChange={(e) => setValue(e.target.value)} autoFocus />
            {!valid && (
                <p className="text-xs text-red-500 mt-1">Enter a whole number between 1 and {max}.</p>
            )}
            <div className="flex gap-3 justify-end mt-4">
                <FpButton variant="secondary" onClick={onClose}>Cancel</FpButton>
                <FpButton variant="primary" disabled={!valid} onClick={() => onConfirm(parsed)}>Quick sell</FpButton>
            </div>
        </FpModal>
    );
}

// Acts on some slice of the stack - the tile's count shrinks as copies get
// unboxed or sold, same as clicking through them one at a time. Copies that are
// listed for trade are locked, so actions only ever touch the unlisted ones.
function StackFooter({ stack, onUnbox, onSellMany }) {
    const pops = stack.filter((p) => !p.listed);
    const listedCount = stack.length - pops.length;
    const target = pops[0];
    const [showQtyModal, setShowQtyModal] = useState(false);

    const listedNote = listedCount > 0 && (
        <p className="font-['Jersey_10'] uppercase text-[11px] tracking-[1px] text-amber-700 mb-1">
            {listedCount} listed for trade
        </p>
    );

    if (!target) {
        return (
            <div>
                {listedNote}
                <Link to="/frankopop/trades" className="block text-center font-['Jersey_10'] uppercase text-[12px] tracking-[1px] text-maroon hover:underline py-1">
                    Manage in My Trades
                </Link>
            </div>
        );
    }

    return (
        <div>
            {listedNote}
            <div className="flex gap-2">
                {target.inCase
                    ? <FpButton variant="primary" size="sm" className="flex-1" onClick={() => onUnbox(target)}>Unbox</FpButton>
                    : <span className="flex-1 text-center font-['Jersey_10'] uppercase text-[12px] tracking-[1px] text-gray-400 py-1">Unpackaged</span>}
                <QuickSellMenu
                    count={pops.length}
                    onSellOne={() => onSellMany(pops.slice(0, 1).map((p) => p.id))}
                    onSellAllButOne={() => onSellMany(pops.slice(0, -1).map((p) => p.id))}
                    onSellQuantity={() => setShowQtyModal(true)}
                />
                {showQtyModal && (
                    <QuickSellQuantityModal
                        max={pops.length}
                        onClose={() => setShowQtyModal(false)}
                        onConfirm={(n) => { setShowQtyModal(false); onSellMany(pops.slice(0, n).map((p) => p.id)); }}
                    />
                )}
            </div>
        </div>
    );
}

// Completion counts are derived from the already-loaded pops list rather than an
// extra fetch per set - keeps this scaling to many sets without N+1 requests.
function SetPicker({ sets, pops, figuresById, activeId, onSelect, search, onSearch }) {
    const filtered = search.trim()
        ? sets.filter((s) => s.name.toLowerCase().includes(search.trim().toLowerCase()))
        : sets;

    return (
        <div className="lg:w-60 shrink-0 flex flex-col gap-2">
            {sets.length > 6 && (
                <TextInput value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Search sets..." />
            )}
            <div className="flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-y-auto lg:max-h-[560px] pb-1 lg:pb-0">
                {filtered.length === 0 && <p className="text-xs text-gray-400 px-1">No sets match.</p>}
                {filtered.map((set) => {
                    const have = new Set(
                        pops.filter((p) => p.setId === set.id && !figuresById.get(p.figureId)?.ltd).map((p) => p.figureId)
                    ).size;
                    const active = set.id === activeId;
                    return (
                        <button
                            key={set.id}
                            type="button"
                            onClick={() => onSelect(set.id)}
                            className={`shrink-0 text-left px-3 py-2 border font-['Jersey_10'] uppercase text-[13px] tracking-[1px] transition-all whitespace-nowrap lg:whitespace-normal ${
                                active ? 'bg-maroon text-white border-maroon' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                            }`}
                        >
                            <div className="flex items-center justify-between gap-3">
                                <span>{set.name}</span>
                                <span className={active ? 'text-white/70' : 'text-gray-400'}>{have}/{set.completableFigureCount}</span>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// LTD figures are a bonus and don't count toward "collected" - still shown in the
// grid, just excluded from the have/total header and progress bar.
function SetSection({ set, figures, myPops, onUnbox, onSellMany }) {
    const popsByFigure = {};
    for (const p of myPops) (popsByFigure[p.figureId] = popsByFigure[p.figureId] || []).push(p);
    const completable = figures.filter((f) => !f.ltd);
    const have = completable.filter((f) => popsByFigure[f.id]?.length).length;

    return (
        <div className="mb-5">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <h3 className="font-['Jersey_10'] uppercase text-[17px] tracking-[1px] text-gray-900">{set.name}</h3>
                <span className="font-['Jersey_10'] text-[14px] text-maroon">{have}/{completable.length} collected</span>
            </div>
            <ProgressBar value={have} max={completable.length} color={set.tint} />

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-3">
                {figures.map((fig) => {
                    const owned = popsByFigure[fig.id] || [];
                    if (owned.length === 0) {
                        return <FigureCard key={fig.id} figure={fig} owned={false} frameSize={150} />;
                    }
                    const boxedStack = owned.filter((p) => p.inCase);
                    const unboxedStack = owned.filter((p) => !p.inCase);
                    return [boxedStack, unboxedStack]
                        .filter((stack) => stack.length > 0)
                        .map((stack) => (
                            <FigureCard
                                key={`${fig.id}-${stack[0].inCase ? 'boxed' : 'unboxed'}`}
                                figure={fig}
                                sealed={stack[0].inCase}
                                corner={`Total: ${stack.length}`}
                                footer={<StackFooter stack={stack} onUnbox={onUnbox} onSellMany={onSellMany} />}
                                frameSize={150}
                            />
                        ));
                })}
            </div>
        </div>
    );
}

export default function FrankoPopMyCollection({ userData }) {
    const navigate = useNavigate();
    const [opening, setOpening] = useState(false);
    const [reveal, setReveal] = useState(null);
    const [selectedSetId, setSelectedSetId] = useState(null);
    const [setSearch, setSetSearch] = useState('');

    const guard = (fn, empty) => (userData ? fn() : Promise.resolve(empty));

    const { data: walletData, refetch: refetchWallet } = useFrankopopData(useCallback(() => guard(fetchWallet, null), [userData]), null);
    const { data: stats, refetch: refetchStats } = useFrankopopData(useCallback(() => guard(fetchMyStats, null), [userData]), null);
    const { data: boxes, refetch: refetchBoxes } = useFrankopopData(useCallback(() => guard(fetchMyBoxes, []), [userData]), []);
    const { data: pops, loading: popsLoading, refetch: refetchPops } = useFrankopopData(useCallback(() => guard(fetchMyPops, []), [userData]), []);

    const wallet = walletData ? walletData.cards : null;
    const popsList = pops || [];

    const setsWithPops = [];
    const seenSetIds = new Set();
    for (const p of popsList) {
        if (p.set && !seenSetIds.has(p.setId)) { seenSetIds.add(p.setId); setsWithPops.push(p.set); }
    }
    const activeSetId = setsWithPops.some((s) => s.id === selectedSetId) ? selectedSetId : setsWithPops[0]?.id;
    const activeSet = setsWithPops.find((s) => s.id === activeSetId) || null;

    const figuresFetch = useCallback(
        () => (activeSetId ? fetchSet(activeSetId).then((r) => r.figures) : Promise.resolve([])),
        [activeSetId]
    );
    const { data: activeFigures, loading: figuresLoading } = useFrankopopData(figuresFetch, []);

    // just enough figure metadata (the ltd flag) to compute completion in the set
    // picker - every pop already carries its own figure, across every set owned
    const figuresById = new Map();
    for (const p of popsList) {
        if (p.figure && !figuresById.has(p.figureId)) figuresById.set(p.figureId, p.figure);
    }

    if (!userData) return <Navigate to="/frankopop/shop" replace />;

    const refetchAfterMutation = () => { refetchWallet(); refetchStats(); refetchPops(); };

    const handleOpen = async (box) => {
        setOpening(true);
        const [res] = await Promise.all([openBox(box.id), wait(600)]);
        setOpening(false);
        if (!res.success) { toast.error(res.message); return; }
        setReveal({ figure: res.figure, set: box.set });
        if (res.scrapsAwarded > 0) toast.success(`Bonus! +${res.scrapsAwarded} ${SCRAP_UNIT}!`);
        refetchBoxes();
        refetchAfterMutation();
    };

    const handleUnbox = async (pop) => {
        const res = await unpackagePop(pop.id);
        if (!res.success) { toast.error(res.message); return; }
        toast.success(`${pop.figure.name} taken out of its case.`);
        refetchStats();
        refetchPops();
    };

    const handleSellMany = async (instanceIds) => {
        const res = await quickSellManyPops(instanceIds);
        if (!res.success) { toast.error(res.message); return; }
        toast.success(`Quick sold ${res.count} FrankoPop${res.count === 1 ? '' : 's'} for ${res.refund} ${FRAMP_UNIT} and ${res.scrapsEarned} ${SCRAP_UNIT}.`);
        refetchAfterMutation();
    };

    return (
        <FrankoPopLayout active="mine" wallet={wallet}>
            <ContentBox theme="default" tint="mediumseagreen" header="Your shelf">
                {stats && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        <StatTile label="Unopened" value={stats.boxes} accent="#c47f17" />
                        <StatTile label="FrankoPops" value={stats.pops} sub={`${stats.unique} unique`} />
                        <StatTile label="Sealed" value={stats.sealed} accent="#9333ea" />
                        <StatTile label="Sets done" value={stats.setsComplete} accent="#16a34a" />
                        <StatTile label="Wallet" value={stats.wallet} sub={CURRENCY} />
                        <StatTile label="Scraps" value={`${stats.scraps}/${stats.maxScraps}`} sub={SCRAP_UNIT} accent="#b45309" />
                    </div>
                )}
            </ContentBox>

            <UnopenedBoxes boxes={boxes || []} onOpened={handleOpen} />

            <ContentBox theme="default" tint="cadetblue" header="Collection">
                {!popsLoading && popsList.length === 0 ? (
                    <EmptyState
                        title="No FrankoPops yet"
                        sub="Buy a box from the shop, or wait for a staff gift, then open it."
                        action={<FpButton variant="primary" onClick={() => navigate('/frankopop/shop')}>Go to the shop</FpButton>}
                    />
                ) : popsList.length > 0 && (
                    <div className="flex flex-col lg:flex-row gap-4">
                        <SetPicker
                            sets={setsWithPops}
                            pops={popsList}
                            figuresById={figuresById}
                            activeId={activeSetId}
                            onSelect={setSelectedSetId}
                            search={setSearch}
                            onSearch={setSetSearch}
                        />
                        <div className="flex-1 min-w-0">
                            {activeSet && (
                                figuresLoading ? (
                                    <p className="text-sm text-gray-500">Loading...</p>
                                ) : (
                                    <SetSection
                                        set={activeSet}
                                        figures={activeFigures || []}
                                        myPops={popsList.filter((p) => p.setId === activeSet.id)}
                                        onUnbox={handleUnbox}
                                        onSellMany={handleSellMany}
                                    />
                                )
                            )}
                        </div>
                    </div>
                )}
            </ContentBox>

            {opening && (
                <FpModal onClose={() => {}} maxWidth="max-w-xs" zIndex={210}>
                    <div className="flex flex-col items-center gap-4 py-4">
                        <img src="https://api.franklyorigins.net/uploads/1789459777160-franko_crate.png" alt="" className="w-24 h-24 object-contain" style={{ imageRendering: 'pixelated', animation: 'fp-shake 0.4s ease-in-out infinite' }} />
                        <p className="font-['Jersey_10'] uppercase text-[18px] tracking-[1px] text-maroon">Opening box…</p>
                    </div>
                    <style>{`@keyframes fp-shake{0%,100%{transform:rotate(-6deg)}50%{transform:rotate(6deg)}}`}</style>
                </FpModal>
            )}

            {reveal && (
                <FpModal title="You pulled…" onClose={() => setReveal(null)} maxWidth="max-w-xs" zIndex={210}>
                    <div className="flex flex-col items-center gap-3">
                        <FigureFrame figure={reveal.figure} size={190} sealed />
                        <div className="text-center">
                            <p className="font-['Jersey_10'] uppercase text-[20px] tracking-[1px] text-coal_dark leading-tight">
                                {reveal.figure.name}
                            </p>
                            <RarityPill name={reveal.figure.rarityName} colour={reveal.figure.rarityColour} className="mt-1" />
                            <p className="text-xs text-gray-500 mt-2">{reveal.set?.name}</p>
                            {reveal.figure.ltd && (
                                <p className="font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-maroon mt-2">
                                    Limited - #{reveal.figure.awarded} of {reveal.figure.ltdTotal}
                                </p>
                            )}
                        </div>
                        <FpButton variant="primary" className="w-full mt-2" onClick={() => setReveal(null)}>Collect</FpButton>
                    </div>
                </FpModal>
            )}
        </FrankoPopLayout>
    );
}
